// After a deploy, a page that has been open since before it can request a
// hashed chunk (or CSS chunk) that no longer exists. Detecting that failure
// and recovering from it - a single automatic reload - is the whole fix for
// "the app crashed and I had to refresh": see docs/NETLIFY_MIGRATION.md,
// §9, for the full write-up of the bug this resolves.
//
// Used from two places, deliberately:
//   - src/index.js: window 'error' (capture) + 'unhandledrejection' listeners,
//     which is the only thing that catches a failed dynamic import() outside
//     React (e.g. @segment/analytics-next's own internal imports).
//   - src/components/ErrorBoundary.js: componentDidCatch, for a failed lazy
//     route chunk that surfaces as a React render error instead.

const STORAGE_KEY = 'rr:chunkReload';
const RETRY_COOLDOWN_MS = 60 * 1000;

const CHUNK_ERROR_PATTERNS = [
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

/**
 * @typedef {{
 *  sha: string,
 *  at: number,
 *  count: number
 * }} ChunkReloadRecord
 */

/**
 * @param {*} error - an Error, a string message, or a raw ErrorEvent (a
 *   failed <script> load surfaces to window.onerror as an Event, not an Error)
 */
export function isChunkLoadError(error) {
  if (!error) return false;

  if (error.name === 'ChunkLoadError') return true;

  if (
    typeof Event !== 'undefined' &&
    error instanceof Event &&
    error.target &&
    error.target.tagName === 'SCRIPT'
  ) {
    return true;
  }

  const message = typeof error === 'string' ? error : error.message;
  if (typeof message !== 'string') return false;

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function getCurrentSha() {
  return process.env.REACT_APP_CURRENT_GIT_SHA || 'unknown';
}

/**
 * @returns {ChunkReloadRecord | null}
 */
function readRecord() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // sessionStorage unavailable (private mode, quota, disabled) - treat as
    // "no prior attempt" rather than throwing.
    return null;
  }
}

/**
 * @param {ChunkReloadRecord} record
 */
function writeRecord(record) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // If we can't persist the record, attemptChunkReload() below still
    // reloads once - it just can't guarantee the loop guard survives it.
  }
}

/**
 * One-shot recovery for a chunk-load failure: reload the page, but at most
 * once per build (REACT_APP_CURRENT_GIT_SHA) per session, and never twice
 * within RETRY_COOLDOWN_MS. A build that is genuinely, persistently broken
 * (not just superseded by a newer deploy) must fall through to the error
 * boundary instead of reload-looping forever.
 *
 * @returns {boolean} whether a reload was triggered
 */
export function attemptChunkReload() {
  const sha = getCurrentSha();
  const now = Date.now();
  const record = readRecord();

  const alreadyTriedThisBuild = !!(
    record &&
    record.sha === sha &&
    record.count >= 1
  );
  const tooSoonSinceLastAttempt = !!(
    record && now - record.at < RETRY_COOLDOWN_MS
  );

  if (alreadyTriedThisBuild || tooSoonSinceLastAttempt) {
    return false;
  }

  writeRecord({ sha, at: now, count: 1 });
  window.location.reload();
  return true;
}

/**
 * Whether a chunk-reload attempt for the *current* build has already been
 * recorded this session. Used by Sentry's beforeSend to drop a duplicate
 * event for an error that is already being recovered from via reload, rather
 * than reporting the same chunk failure twice.
 */
export function hasAttemptedChunkReloadThisSession() {
  const sha = getCurrentSha();
  const record = readRecord();
  return !!(record && record.sha === sha && record.count >= 1);
}
